import {Injectable} from "@angular/core";
import {InteractionType} from "~/app/core/model/interaction-type";
import {InteractionRefType} from "~/app/core/model/interaction-ref-type";
import {InteractionInfo} from "~/app/core/model/interaction-info";
import {toDateStr} from "~/app/core/model/date-str";
import * as Log from "~/app/core/log";
import {InteractionComponent} from "~/app/core/model/interaction-component";
import {UserInfo} from "~/app/core/model/user-info";
import {InteractionOffer} from "~/app/core/model/interaction/interaction-offer";
import {StorageService} from "~/app/core/storage.service";
import {InteractionAd} from "~/app/core/model/interaction/interaction-ad";
import {InteractionOther} from "~/app/core/model/interaction/interaction-other";
import {SettingsService} from "~/app/core/settings.service";
import {Settings} from "~/app/core/model/settings";
import {AngularFireAnalytics} from "@angular/fire/compat/analytics";

declare var UIApplication;
declare var android;

export class InteractionTimer {
    log: (done?: boolean) => void;
    reset: () => void;
}

export interface LogOpComplexEventOptions {
    key: string,
    parameters: [{
        key: string;
        value: any;
        type: string;
    }];
}

@Injectable()
export class InteractionService {
    private _flushQueue: () => void;
    private _timers: Array<InteractionTimer> = new Array();

    constructor(
        // private readonly firebaseAnalytics: AngularFireAnalytics,
        private readonly storage: StorageService,
        private readonly settingsService: SettingsService
    ) {
        // do nothing
    }

    get settings(): Settings {
        return this.settingsService.loadSettings();
    }

    registerFlushQueue(flushQueue: () => void) {
        this._flushQueue = flushQueue;
    }

    flushQueue() {
        if (this._flushQueue) {
            this._flushQueue();
        }
    }

    interactOffer(offerId: number, data: InteractionOffer) {
        if (offerId && data) {
            const aggr = this.storage.loadInteraction();
            if (!aggr.offers) {
                aggr.offers = {};
            }
            let offer: InteractionOffer = aggr.offers[String(offerId)];
            if (!offer) {
                offer = new InteractionOffer();
                aggr.offers[String(offerId)] = offer;
            }
            InteractionOffer.merge(offer, data);
            this.storage.storeInteraction(aggr);
            this.flushQueue();
        }
    }

    interactAd(adId: number, data: InteractionAd) {
        if (adId && data) {
            const aggr = this.storage.loadInteraction();
            if (!aggr.ads) {
                aggr.ads = {};
            }
            let ad: InteractionAd = aggr.ads[String(adId)];
            if (!ad) {
                ad = new InteractionAd();
                aggr.ads[String(adId)] = ad;
            }
            InteractionAd.merge(ad, data);
            this.storage.storeInteraction(aggr);
            this.flushQueue();
        }
    }

    interactOther(data: InteractionOther) {
        if (data) {
            const aggr = this.storage.loadInteraction();
            let other: InteractionOther = aggr.other;
            if (!other) {
                other = new InteractionOther();
                aggr.other = other;
            }
            InteractionOther.merge(other, data);
            this.storage.storeInteraction(aggr);
            this.flushQueue();
        }
    }

    interactTimerOffer(offerId: number, timerField: "pgViewTime"|"pgOpenTime"|"pgShareTime"|"pgShowCashierTime"): InteractionTimer {
        const timer = this.interactTimer();
        const func = {
            tracking: timerField,
            log: (done: boolean = true) => {
                const interaction: InteractionOffer = {};
                interaction[timerField] = timer();
                if (offerId) this.interactOffer(offerId, interaction);
                if (done) {
                    this._timers = this._timers.filter((item) => item !== func);
                }
            },
            reset: () => {
                timer();
            }
        };
        this._timers.push(func);
        return func;
    }

    interactTimerAd(adId: number, timerField: "pgViewTime"|"pgVideoTime"|"pgThanksTime"|"pgSponsorTime"): InteractionTimer {
        const timer = this.interactTimer();
        const func = {
            tracking: timerField,
            log: (done: boolean = true) => {
                const interaction: InteractionAd = {};
                interaction[timerField] = timer();
                if (adId) this.interactAd(adId, interaction);
                if (done) {
                    this._timers = this._timers.filter((item) => item !== func);
                }
            },
            reset: () => {
                timer();
            }
        };
        this._timers.push(func);
        return func;
    }

    interactTimerOther(timerField: "pgWelcomeTime"|"pgAboutTime"|"pgSettingsTime"|"pgNoOffersTime"|"pgLocationTime"): InteractionTimer {
        const timer = this.interactTimer();
        const func = {
            tracking: timerField,
            log: (done: boolean = true) => {
                const interaction: InteractionOther = {};
                interaction[timerField] = timer();
                this.interactOther(interaction);
                if (done) {
                    this._timers = this._timers.filter((item) => item !== func);
                }
            },
            reset: () => {
                timer();
            }
        };
        this._timers.push(func);
        return func;
    }

    pauseTimers(): void {
        this._timers.forEach((timer) => {
            timer.log(false);
        });
    }

    resumeTimers(): void {
        this._timers.forEach((timer) => {
            timer.reset();
        });
    }

    interaction(type: InteractionType, comp: InteractionComponent, refType?: InteractionRefType, ref?: number) {
        if (this.settings.env === "prod") {
            Log.setTagLogLevel(Log.Tag.InteractionService, Log.Level.trace);
            if (typeof ref === "undefined") {
                ref = null;
            }
            const info: InteractionInfo = {
                type,
                comp,
                refType,
                ref,
                ts: toDateStr(new Date())
            };
            if (Log.isTrace(Log.Tag.InteractionService)) {
                let message = "";
                if (refType !== null || ref !== null) {
                    message += "[";
                    if (refType !== null) {
                        message += refType;
                    }
                    if (ref !== null) {
                        if (refType !== null) {
                            message += "/";
                        }
                        message += ref;
                    }
                    message += "]";
                }
                if (message.length > 0) {
                    message += " ";
                }
                message += type;
                if (comp !== null) {
                    message += " ";
                    message += comp;
                }
                Log.trace(Log.Tag.InteractionService, message);

                // type: InteractionType;
                // comp: InteractionComponent;
                // refType: InteractionRefType;
                // ref: number;
                // ts: DateStr;
                // seconds: number;

                const firebaseEvent: LogOpComplexEventOptions = {
                    key: refType ? type + "_" + refType : type,
                    parameters: [
                        { key: "component", value: comp, type: "string" }
                    ]
                };

                if (refType) {
                    switch (refType) {
                        case InteractionRefType.Sponsor : {
                            if (ref) firebaseEvent.parameters.push({ key: "sponsor_id", value: ref, type: "long" });
                            break;
                        }
                        case InteractionRefType.Merchant : {
                            if (ref) firebaseEvent.parameters.push({ key: "merchant_id", value: ref, type: "long" });
                            break;
                        }
                        case InteractionRefType.Offer : {
                            if (ref) firebaseEvent.parameters.push({ key: "offer_id", value: ref, type: "long" });
                            break;
                        }
                        case InteractionRefType.GPS : {
                            if (ref) firebaseEvent.parameters.push({ key: "gps", value: 1, type: "long" });
                            break;
                        }
                        case InteractionRefType.ZipCode : {
                            if (ref) firebaseEvent.parameters.push({ key: "zip_code", value: ref, type: "long" });
                            break;
                        }
                    }
                }

                Log.trace(Log.Tag.InteractionService, firebaseEvent);
                // this.firebaseAnalytics.logEvent(firebaseEvent.key,XXXX );
            }
        }
    }

    updatePage(pageName: string) {
        if (this.settings.env === "prod") {
            // this.firebaseAnalytics.setCurrentScreen(pageName).then(() => {
            //     Log.debug(Log.Tag.InteractionService, "Path: " + pageName);
            // });
        }
    }

    updateUser(userInfo: UserInfo) {
        if (this.settings.env === "prod") {
            // if (userInfo) {
            //     this.firebaseAnalytics.setUserId(String(userInfo.id));
            // } else {
            //     this.firebaseAnalytics.setUserId(null);
            // }
        }
    }

    private interactTimer(): () => number {
        const maxElapsed = 60 * 1000 * 15;
        let startMillis = new Date().getTime();
        return (): number => {
            const stopMillis = new Date().getTime();
            let elapsed = stopMillis - startMillis;
            if (elapsed > maxElapsed) {
                elapsed = maxElapsed;
            }

            startMillis = new Date().getTime();

            return Math.round(elapsed / 1000);
        };
    }
}
